import { Typography } from "@mui/material";
import { gridRowNodeSelector, useGridApiContext, type GridRenderCellParams } from "@mui/x-data-grid-premium";
import type { TabularDataCell } from "../../../../../../api/types/svTypes";
import type { AgingDetailColumn, AgingDetailGroupTotal } from "../types/agingDetailTypes";
import { PERIOD_GUID, type AgingDetailGridRow } from "./AgingDetailTable.types";

interface Props {
  params: GridRenderCellParams<AgingDetailGridRow>;
  col: AgingDetailColumn;
  groupTotals: AgingDetailGroupTotal[] | undefined;
  grandTotals: Record<string, TabularDataCell> | null;
  onDrillDown?: (cell: TabularDataCell) => void;
}
const FooterDataCell = ({ params, col, groupTotals, grandTotals, onDrillDown }: Props) => {
  const apiRef = useGridApiContext();
  const cell = resolveAggregationCell(params.id, col.guid, apiRef, groupTotals ?? [], grandTotals);
  const formattedValue = cell?.formattedValue ?? "";
  const canDrillDown = !!cell?.drillDownId && !!onDrillDown;

  return (
    <Typography
      variant="subtitle2"
      sx={{
        textAlign: "right",
        width: "100%",
        ...(canDrillDown ? { cursor: "pointer", "&:hover": { textDecoration: "underline" } } : {}),
      }}
      onClick={() => canDrillDown && cell && onDrillDown(cell)}
    >
      {formattedValue}
    </Typography>
  );
};

export default FooterDataCell;

function resolveAggregationCell(
  rowId: GridRenderCellParams["id"],
  fieldGuid: string,
  apiRef: ReturnType<typeof useGridApiContext>,
  groupTotals: AgingDetailGroupTotal[],
  grandTotals: Record<string, TabularDataCell> | null
): TabularDataCell | undefined {
  const rowNode = gridRowNodeSelector(apiRef, rowId);
  if (rowNode) {
    const parentId = "parent" in rowNode ? (rowNode.parent as string | null) : null;
    if (parentId != null) {
      const parentNode = gridRowNodeSelector(apiRef, parentId);
      if (parentNode?.type === "group" && parentNode.groupingKey != null) {
        const groupingKey = parentNode.groupingKey.toString();
        const groupTotal = groupTotals.find((gt) => gt.filters[PERIOD_GUID]?.value?.toString() === groupingKey);
        if (groupTotal) {
          return groupTotal.values[fieldGuid] as TabularDataCell | undefined;
        }
      }
    }
  }

  // Fallback: grand total
  return grandTotals?.[fieldGuid];
}
